import { useEffect, useState } from "react";

import {
  type SocialLink,
  useAdminSocialLinks,
  useCreateSocialLink,
  useDeleteSocialLink,
  useReorderSocialLinks,
  useUpdateSocialLink,
} from "@/lib/queries/use-social-links";

interface SocialLinkFormData {
  platform: string;
  label: string;
  url: string;
  visible: boolean;
}

const emptyForm: SocialLinkFormData = {
  platform: "",
  label: "",
  url: "",
  visible: true,
};

// Common platforms with icons
const PLATFORMS = [
  { id: "x", label: "X (Twitter)" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "github", label: "GitHub" },
  { id: "email", label: "Email" },
  { id: "instagram", label: "Instagram" },
  { id: "youtube", label: "YouTube" },
  { id: "website", label: "Website" },
  { id: "other", label: "Other" },
];

// Square knob toggle component
function VisibilityToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 transition-colors ${
        checked ? "bg-green-600" : "bg-border-hover"
      }`}
    >
      <div
        className={`absolute top-0 w-5 h-5 bg-white transition-transform ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

export function AdminSocialLinks() {
  const { data: socialLinks, isLoading } = useAdminSocialLinks();
  const createSocialLink = useCreateSocialLink();
  const updateSocialLink = useUpdateSocialLink();
  const deleteSocialLink = useDeleteSocialLink();
  const reorderSocialLinks = useReorderSocialLinks();

  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [formData, setFormData] = useState<SocialLinkFormData>(emptyForm);
  const [localLinks, setLocalLinks] = useState<SocialLink[]>([]);

  // Sync local state with server data
  useEffect(() => {
    if (socialLinks) {
      setLocalLinks(socialLinks);
    }
  }, [socialLinks]);

  const handleEdit = (item: SocialLink) => {
    setEditingId(item.id);
    setFormData({
      platform: item.platform,
      label: item.label,
      url: item.url,
      visible: item.visible,
    });
  };

  const handleNew = () => {
    setEditingId("new");
    setFormData(emptyForm);
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleSave = async () => {
    if (editingId === "new") {
      const maxOrder = localLinks.reduce((max, l) => Math.max(max, l.display_order), -1);
      await createSocialLink.mutateAsync({
        platform: formData.platform,
        label: formData.label,
        url: formData.url,
        visible: formData.visible,
        display_order: maxOrder + 1,
      });
    } else if (editingId !== null) {
      await updateSocialLink.mutateAsync({
        id: editingId,
        platform: formData.platform,
        label: formData.label,
        url: formData.url,
        visible: formData.visible,
      });
    }
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleDelete = async (id: number) => {
    if (confirm("Are you sure you want to delete this link?")) {
      await deleteSocialLink.mutateAsync(id);
    }
  };

  const handleToggleVisibility = async (item: SocialLink) => {
    await updateSocialLink.mutateAsync({
      id: item.id,
      platform: item.platform,
      label: item.label,
      url: item.url,
      visible: !item.visible,
    });
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newOrder = [...localLinks];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setLocalLinks(newOrder);
    await reorderSocialLinks.mutateAsync(newOrder.map((l) => l.id));
  };

  const handleMoveDown = async (index: number) => {
    if (index === localLinks.length - 1) return;
    const newOrder = [...localLinks];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setLocalLinks(newOrder);
    await reorderSocialLinks.mutateAsync(newOrder.map((l) => l.id));
  };

  // Extract display URL without protocol
  const getDisplayUrl = (url: string) => {
    return url.replace(/^(https?:\/\/)?(mailto:)?/, "").slice(0, 30);
  };

  if (isLoading) {
    return (
      <div>
        <p className="text-text-muted">Loading social links...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="text-[24px] md:text-[28px] font-bold text-primary">Social Links</h1>
        <button
          onClick={handleNew}
          className="px-4 py-2 bg-primary text-background text-[13px] font-bold hover:opacity-90 active:opacity-75 transition-opacity"
        >
          + Add Link
        </button>
      </div>

      {/* Edit Form */}
      {editingId !== null && (
        <div className="border border-border bg-card p-4 md:p-6 mb-6">
          <h2 className="text-[11px] text-text-muted font-bold tracking-caps uppercase mb-4">
            {editingId === "new" ? "New Social Link" : "Edit Social Link"}
          </h2>

          <div className="space-y-4 max-w-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                  Platform
                </label>
                <div className="relative">
                  <select
                    value={formData.platform}
                    onChange={(e) => {
                      const platform = e.target.value;
                      setFormData({
                        ...formData,
                        platform,
                        label:
                          formData.label ||
                          PLATFORMS.find((p) => p.id === platform)?.label?.split(" ")[0] ||
                          "",
                      });
                    }}
                    className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle appearance-none cursor-pointer transition-colors"
                  >
                    <option value="">Select platform...</option>
                    {PLATFORMS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg
                      className="size-4 text-text-primary"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 9l6 6 6-6" strokeLinecap="square" strokeLinejoin="miter" />
                    </svg>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                  Display Label
                </label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                  placeholder="e.g., X, LinkedIn, Email"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                URL
              </label>
              <input
                type="text"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                placeholder={
                  formData.platform === "email" ? "mailto:you@example.com" : "https://..."
                }
              />
            </div>

            <div className="flex items-center gap-3">
              <VisibilityToggle
                checked={formData.visible}
                onChange={(checked) => setFormData({ ...formData, visible: checked })}
              />
              <span className="text-[13px] text-text-secondary">
                {formData.visible ? "Visible on site" : "Hidden from site"}
              </span>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={!formData.platform || !formData.label || !formData.url}
                className="px-4 py-2 bg-primary text-background text-[13px] font-bold hover:opacity-90 active:opacity-75 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingId === "new" ? "Create" : "Save"}
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-border text-text-secondary text-[13px] hover:bg-card active:bg-border transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Social Links List */}
      <div className="space-y-2 max-w-2xl">
        {localLinks.map((item, index) => (
          <div
            key={item.id}
            className="p-3 md:p-4 border border-border bg-card hover:border-border-hover transition-colors"
          >
            <div className="flex items-start gap-3">
              {/* Reorder buttons */}
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  className="text-text-faint hover:text-text-secondary hover:bg-border active:bg-border-hover disabled:opacity-30 disabled:cursor-not-allowed p-1.5 transition-colors"
                  title="Move up"
                >
                  <svg className="size-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => handleMoveDown(index)}
                  disabled={index === localLinks.length - 1}
                  className="text-text-faint hover:text-text-secondary hover:bg-border active:bg-border-hover disabled:opacity-30 disabled:cursor-not-allowed p-1.5 transition-colors"
                  title="Move down"
                >
                  <svg className="size-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>

              {/* Content - stacked on mobile, inline on desktop */}
              <div className="flex-1 min-w-0">
                {/* Mobile layout: stacked */}
                <div className="md:hidden">
                  <p className="text-[14px] text-text-primary font-medium">{item.label}</p>
                  <p className="text-[11px] text-text-faint truncate mt-0.5">
                    {getDisplayUrl(item.url)}
                  </p>
                  <div className="flex items-center gap-4 mt-2.5">
                    <VisibilityToggle
                      checked={item.visible}
                      onChange={() => handleToggleVisibility(item)}
                    />
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-[12px] text-text-muted hover:text-primary active:text-primary/80 px-2 py-1 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-[12px] text-text-muted hover:text-destructive active:text-destructive/80 px-2 py-1 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Desktop layout: inline */}
                <div className="hidden md:flex md:items-center md:gap-4">
                  <div className="flex-1 min-w-0">
                    <span className="text-[13px] text-text-primary font-medium">{item.label}</span>
                    <span className="text-[11px] text-text-faint ml-2 truncate">
                      {getDisplayUrl(item.url)}
                    </span>
                  </div>
                  <VisibilityToggle
                    checked={item.visible}
                    onChange={() => handleToggleVisibility(item)}
                  />
                  <button
                    onClick={() => handleEdit(item)}
                    className="text-[12px] text-text-muted hover:text-primary active:text-primary/80 px-3 py-2 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="text-[12px] text-text-muted hover:text-destructive active:text-destructive/80 px-3 py-2 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {localLinks.length === 0 && (
          <div className="border border-dashed border-border-hover py-12 text-center">
            <p className="text-text-muted text-[14px]">No social links added yet.</p>
            <button onClick={handleNew} className="mt-3 text-[13px] text-primary hover:underline">
              + Add your first link
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
